/**
 * EvaluationWorkspace — M6 Output Factory · Increment 2 (2026-06-25, Anil).
 *
 * The staff EVALUATION surface: the per-tier checklist of finding types, each row
 * showing its source-tag + confirm-loop status, and a Confirm action that flips a
 * finding to POOL-READY. The confirmed findings are the single source the report
 * (Inc 3) draws from. UI contract: `AVBP_EVALUATION_TO_REPORT_PIPELINE_MOCKUP_2026-06-25.html` §1.
 *
 * Reuse vs stub (Inc 2): map_review / grid_selection / zone_auo already have REAL
 * evaluation outputs (StaffTechnicalPanel + tracer + AUO confirm-loop) — they
 * surface here as findings to confirm into the pool. astro_vastu / astro_remedies
 * / health_overlay / verdict are NEW confirmable records (a record that holds /
 * references the data; deep astro-engine wiring is Inc 3).
 *
 * Confirm-loop (owner ruling): AI drafts where it can; nothing is `confirmed`
 * without a staff confirm.
 *
 * Injectable seams (so the no-login preview harness drives it with mock data):
 * `findingsOverride` supplies findings; `onConfirm` overrides the confirm write.
 * In production both are omitted → it streams live findings + writes via
 * findingsStore.
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Btn3, Tag } from './ui';
import type { Role } from './../lib/roles';
import {
  FINDING_META,
  FINDING_IS_REAL,
  evaluationChecklistForTier,
  checklistRequired,
  checklistTypesForTier,
  evaluationProgress,
  blankFinding,
  isPoolReady,
  type Finding,
  type FindingType,
  type FindingStatus,
} from '../lib/findingsModel';
import {
  listenProjectFindings,
  confirmFinding,
  reopenFinding,
} from '../lib/findingsStore';

export interface EvaluationWorkspaceProps {
  clientId: string | null;
  projectId: string;
  tierCode: string;
  role: Role | null;
  actorUid?: string | null;
  onMsg?: (m: string) => void;
  /** PREVIEW seam — supply findings instead of streaming Firebase. */
  findingsOverride?: Finding[];
  /** PREVIEW seam — override the confirm write (no real CF). */
  onConfirm?: (type: FindingType) => Promise<void> | void;
  /**
   * 2.3 — FOLDED mode: render the pool-ready gate + the confirm-loop rows INLINE
   * inside the producing step (the L1A per-property evaluation), WITHOUT the
   * standalone titled "Evaluation Workspace" Box framing or the verbose Increment
   * footer — so there is no duplicate "0/N" panel. The gate + confirms are kept;
   * only the redundant chrome is dropped. Defaults false (the standalone panel).
   */
  folded?: boolean;
}

const STATUS_TAG: Record<FindingStatus, { kind: 'val' | 'warn' | 'tag'; text: string }> = {
  confirmed: { kind: 'val', text: 'Confirmed' },
  ai_drafted: { kind: 'warn', text: 'AI-drafted · confirm' },
  to_assess: { kind: 'tag', text: 'To assess' },
};

const SOURCE_LABEL: Record<string, string> = { auto: 'AUTO', manual: 'MANUAL', composite: 'COMPOSITE' };

export default function EvaluationWorkspace({
  clientId,
  projectId,
  tierCode,
  role,
  actorUid,
  onMsg,
  findingsOverride,
  onConfirm,
  folded = false,
}: EvaluationWorkspaceProps) {
  const mayConfirm = role === 'admin' || role === 'staff';
  const types = useMemo(() => checklistTypesForTier(tierCode), [tierCode]);
  const checklist = useMemo(() => evaluationChecklistForTier(tierCode), [tierCode]);

  const [liveFindings, setLiveFindings] = useState<Finding[]>([]);
  useEffect(() => {
    if (findingsOverride) return; // preview mode — no Firebase
    if (!clientId || !projectId) { setLiveFindings([]); return; }
    const unsub = listenProjectFindings(clientId, projectId, setLiveFindings, () => {});
    return unsub;
  }, [findingsOverride, clientId, projectId]);
  const findings = findingsOverride ?? liveFindings;

  const byType = useMemo(() => {
    const m = new Map<FindingType, Finding>();
    for (const f of findings) m.set(f.type, f);
    return m;
  }, [findings]);

  const progress = useMemo(() => evaluationProgress(tierCode, findings), [tierCode, findings]);
  const [busy, setBusy] = useState<FindingType | null>(null);

  async function doConfirm(type: FindingType) {
    if (busy) return;
    if (onConfirm) { await onConfirm(type); return; } // preview seam
    if (!clientId) { onMsg?.('No client linked to this project.'); return; }
    setBusy(type);
    try {
      await confirmFinding(clientId, projectId, type, actorUid || 'staff');
      onMsg?.(`${FINDING_META[type].label} confirmed → pool-ready.`);
    } catch (e) {
      onMsg?.(`Confirm failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function doReopen(type: FindingType) {
    if (busy || !clientId) return;
    setBusy(type);
    try {
      await reopenFinding(clientId, projectId, type, actorUid || 'staff');
      onMsg?.(`${FINDING_META[type].label} re-opened.`);
    } catch (e) {
      onMsg?.(`Re-open failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  const inner = (
    <>
      {/* progress banner */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          border: '1px solid var(--accent-line, #d3e0ec)', background: 'var(--accent-wash, #eef3f8)',
          borderRadius: 'var(--r-sm)', padding: '10px 13px', marginBottom: 12,
        }}
      >
        <Tag kind={progress.complete ? 'val' : 'warn'}>
          {progress.confirmedRequired.length} / {progress.requiredTypes.length} required confirmed
        </Tag>
        <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: 'var(--accent-deep, #274b63)' }}>
          Assess once into the pool — the report (next stage) is then built by selection from the <b>confirmed</b> findings.
          {progress.complete
            ? ' All required evaluations are confirmed — pool ready.'
            : ` Outstanding: ${progress.outstandingRequired.map((t) => FINDING_META[t].label).join(', ') || '—'}.`}
        </div>
      </div>

      {/* checklist rows */}
      {types.map((type) => {
        const f = byType.get(type) ?? blankFinding(projectId, type);
        const status = f.status as FindingStatus;
        const required = checklistRequired(checklist[type]);
        const meta = FINDING_META[type];
        const st = STATUS_TAG[status];
        const ready = isPoolReady(status);
        const realToday = FINDING_IS_REAL[type];
        return (
          <div
            key={type}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)',
              padding: '9px 12px', margin: '7px 0',
              background: ready ? 'var(--ok-wash, #eaf5ef)' : 'var(--surface)',
            }}
          >
            <span style={{ width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, background: 'var(--accent-wash, #eef3f8)', border: '1px solid var(--accent-line, #d3e0ec)', flex: '0 0 auto' }}>
              {meta.icon}
            </span>
            <div style={{ minWidth: 160 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {meta.label}
                {required && <span style={{ color: 'var(--red, #9a3535)', marginLeft: 5, fontSize: 11 }}>required</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-mute)' }}>{meta.meta}</div>
            </div>
            <span style={{ flex: 1 }} />
            {/* source tag */}
            <span style={{ fontSize: 9.5, fontWeight: 700, borderRadius: 999, padding: '1px 7px', background: f.source === 'manual' ? 'var(--purple-wash, #f0edf7)' : f.source === 'composite' ? 'var(--warn-wash, #fbf3e1)' : 'var(--accent-wash, #eef3f8)', color: f.source === 'manual' ? 'var(--purple, #5b4b8a)' : f.source === 'composite' ? 'var(--warn, #9a6a12)' : 'var(--accent-deep, #274b63)' }}>
              {SOURCE_LABEL[f.source] || f.source.toUpperCase()}
            </span>
            {!realToday && (
              <Tag kind="tag" title="A confirmable record this increment; deep astro/health engine wiring is Increment 3.">record</Tag>
            )}
            <Tag kind={st.kind}>{st.text}</Tag>
            {mayConfirm && (
              ready ? (
                <Btn3 intent="ghost" size="sm" onClick={() => doReopen(type)} disabled={busy === type}>Re-open</Btn3>
              ) : (
                <Btn3 intent="grn" size="sm" onClick={() => doConfirm(type)} disabled={busy === type}>
                  {busy === type ? 'Confirming…' : status === 'ai_drafted' ? 'Confirm' : 'Confirm'}
                </Btn3>
              )
            )}
          </div>
        );
      })}

      {/* 2.3 — the verbose Increment footer is dropped in FOLDED mode (the confirm
          rows now live inside the per-property step; no standalone duplicate). */}
      {!folded && (
        <div style={{ marginTop: 11, fontSize: 11, color: 'var(--ink-mute)', borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
          AI drafts where it can; <b>nothing reaches a client report until staff confirm</b> (the AUO confirm-loop).
          The checklist is shorter for L1, fuller for L3 — driven by the tier, same as the intake's section flags.
          <div style={{ marginTop: 4 }}>
            <b>Increment 2 (scaffolding):</b> the Findings Pool + per-tier checklist + confirm-loop. Map / grid / zone-AUO reuse the
            existing evaluation panels; astro-vastu / remedies / health / verdict are confirmable records (deep engine wiring = Inc 3).
          </div>
        </div>
      )}
    </>
  );

  // 2.3 — FOLDED: a slim, untitled wrapper inside the per-property step (no
  // standalone "Evaluation Workspace" Box). STANDALONE (non-L1A): the original
  // titled panel, byte-identical.
  if (folded) {
    return (
      <div data-testid="evaluation-confirm-folded" style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
          Confirm into the findings pool
          <span style={{ fontWeight: 500, color: 'var(--ink-mute)' }}>
            {' '}· the report reads only confirmed findings
          </span>
        </div>
        {inner}
      </div>
    );
  }

  return (
    <Box
      title="Evaluation Workspace"
      note={`Findings Pool · the per-tier evaluation checklist · ${tierCode || 'tier'}`}
      allowOverflow
    >
      {inner}
    </Box>
  );
}
